import { useEffect, type ReactNode } from 'react';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import { datesSkirt } from './dates_skirt.js';
import { fmtWeekdayDayMonth } from './planner_dates.js';
import {
  WhenSheetFooter,
  legSheetHeading,
  whenSheetTitle,
  type WhenSheetSlots,
} from './when_sheet.js';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { PlannerStatus } from './planner_status.js';
import { PlannerWhenField } from './planner_collapsed.js';
import { whenMirrorClass } from './when_trigger.js';
import type { PlannerBodyProps } from './planner_form_model.js';
import type { PlannerState } from './planner_state.js';
import type { MultiPlanner } from './multi_planner.js';
import { BinaryCalendarView } from './planner_calendars.js';
import type { FlightPlannerCopy } from './planner_copy.js';

/*
 * O campo QUANDO do planner em cada degrau de layout, com a grade dentro: o
 * binário e o de cada linha do multi. Saíram de `planner_form.tsx` pelo teto
 * de linhas dele.
 */

/** Nada a fazer ao abrir/fechar — o popover de datas do layout aberto é
 *  NÃO-transacional (#2136, a régua do desenho): o clique no dia já escreve o
 *  estado vivo, fechar por qualquer porta guarda o que foi escolhido, e quem
 *  desfaz é o Redefinir da saia. Módulo (não arrow inline) pra identidade
 *  estável. */
const KEEP_DATES = () => {};

function WhenFieldView({
  band,
  readOnly,
  note,
  label,
  value,
  pendingTail,
  hasOutbound,
  ready,
  ctaLabel,
  confirmDatesLabel,
  calendar,
  openDates,
  restoreDates,
  submitDates,
  open,
  oneWay,
  whenOpen,
  onWhenOpenChange,
  skirtDisabled,
  resetLabel,
  resetDates,
  floatDatesPopover = false,
  sheetDates,
  backLabel,
  wanted,
}: {
  readonly band: boolean;
  readonly readOnly: boolean;
  readonly note?: string;
  readonly label: string;
  readonly value: string;
  /** A meia-resposta do ida e volta ("– Volta") — ver `PlannerWhenField`. */
  readonly pendingTail?: string;
  readonly hasOutbound: boolean;
  readonly ready: boolean;
  readonly ctaLabel: string;
  /** O rótulo do primário — nomeia o par quando fechado (#2106). */
  readonly confirmDatesLabel: string;
  readonly calendar: ReactNode;
  readonly openDates: () => void;
  readonly restoreDates: () => void;
  /** O primário do popover; no layout aberto, o Confirmar da saia. */
  readonly submitDates: () => void;
  /** Layout ABERTO (card do chat / tela determinística): popover controlado,
   *  não-transacional, saia só no intervalo (#2136). `false` = faixa/colapsado,
   *  com o comportamento de sempre. */
  readonly open: boolean;
  readonly oneWay: boolean;
  readonly whenOpen: boolean;
  readonly onWhenOpenChange: (open: boolean) => void;
  readonly skirtDisabled: boolean;
  readonly resetLabel: string;
  readonly resetDates: () => void;
  readonly floatDatesPopover?: boolean;
  /** Layout aberto no palco estreito: o QUANDO abre em folha (#2653) com estes
   *  slots (título, barra de meses, rodapé — #3446). Ausente ⇒ popover. */
  readonly sheetDates: WhenSheetSlots | undefined;
  readonly backLabel: string;
  /** O QUANDO pede a vez (o anel do preenchimento guiado) — só o layout aberto. */
  readonly wanted: boolean;
}) {
  // 'end' é a régua de sempre (`.fpl-band .fpl-pop`/`.fpl-filter .fpl-pop`, CSS):
  // o QUANDO é a última coluna da fileira em toda forma menos o colapsado puro
  // (nenhum host em produção — ver a doc de `layout`), que não tem override e
  // alinha pela esquerda. `PlannerWhenField.align` reproduz esta MESMA régua em
  // JS pra quando o popover sai por portal (`floatDatesPopover`): a âncora
  // CSS-por-ancestral não alcança um nó que já não é descendente do ancestral.
  const align: 'start' | 'end' = open || band ? 'end' : 'start';
  if (open) {
    return (
      <OpenWhenField
        readOnly={readOnly}
        label={label}
        value={value}
        {...(pendingTail ? { pendingTail } : {})}
        hasOutbound={hasOutbound}
        confirmDatesLabel={confirmDatesLabel}
        oneWay={oneWay}
        whenOpen={whenOpen}
        onWhenOpenChange={onWhenOpenChange}
        skirtDisabled={skirtDisabled}
        resetLabel={resetLabel}
        resetDates={resetDates}
        float={floatDatesPopover}
        align={align}
        sheet={sheetDates}
        backLabel={backLabel}
        wanted={wanted}
        onConfirm={submitDates}
        openDates={openDates}
        restoreDates={restoreDates}
      >
        {calendar}
      </OpenWhenField>
    );
  }
  return (
    <PlannerWhenField
      label={label}
      value={value}
      {...(pendingTail ? { pendingTail } : {})}
      empty={!hasOutbound}
      disabled={readOnly}
      panelLabel={label}
      confirmLabel={band ? confirmDatesLabel : ctaLabel}
      confirmDisabled={band ? false : !ready}
      {...(note ? { note } : {})}
      onOpen={openDates}
      onDismiss={restoreDates}
      {...(band ? {} : { onConfirm: submitDates })}
      float={floatDatesPopover}
      align={align}
    >
      {calendar}
    </PlannerWhenField>
  );
}

/** A FOLHA do celular é TRANSACIONAL (#5662): o que se toca nela grava no
 *  estado vivo (a grade, o mapa de preços e o guia leem dele), mas só o
 *  Confirmar — ou o toque do dia na somente ida — fica. Voltar, Escape e o gesto
 *  do sistema devolvem as datas da abertura. O retrato é tirado quando a folha
 *  abre, venha a abertura do gatilho ou do guia (`whenOpen` controlado). */
function useSheetSnapshot(sheetOpen: boolean, openDates: () => void): void {
  // A troca da callback fica no commit (layout effect, antes deste efeito
  // passivo): um render descartado não retrata datas que nunca chegaram à tela.
  const takeSnapshot = useStableCallback(openDates);
  useEffect(() => {
    if (sheetOpen) takeSnapshot();
  }, [sheetOpen, takeSnapshot]);
}

/** O QUANDO do layout ABERTO (#2136): popover controlado, não-transacional, saia
 *  só no intervalo — e, na folha do celular, transacional (`useSheetSnapshot`).
 *  Componente próprio pelo teto de complexidade do `WhenFieldView`, que segue
 *  dono dos degraus faixa/colapsado. */
function OpenWhenField({
  readOnly,
  label,
  value,
  pendingTail,
  hasOutbound,
  confirmDatesLabel,
  oneWay,
  whenOpen,
  onWhenOpenChange,
  skirtDisabled,
  resetLabel,
  resetDates,
  float,
  align,
  sheet,
  backLabel,
  wanted,
  onConfirm,
  openDates,
  restoreDates,
  children,
}: {
  readonly readOnly: boolean;
  readonly label: string;
  readonly value: string;
  readonly pendingTail?: string;
  readonly hasOutbound: boolean;
  readonly confirmDatesLabel: string;
  readonly oneWay: boolean;
  readonly whenOpen: boolean;
  readonly onWhenOpenChange: (open: boolean) => void;
  readonly skirtDisabled: boolean;
  readonly resetLabel: string;
  readonly resetDates: () => void;
  readonly float: boolean;
  readonly align: 'start' | 'end';
  readonly sheet: WhenSheetSlots | undefined;
  readonly backLabel: string;
  readonly wanted: boolean;
  /** O Confirmar da saia, depois de fechar (ver `datesSkirt`). */
  readonly onConfirm: () => void;
  /** Retrato e restauro das datas — só a folha os usa. */
  readonly openDates: () => void;
  readonly restoreDates: () => void;
  readonly children: ReactNode;
}) {
  useSheetSnapshot(sheet !== undefined && whenOpen && !readOnly, openDates);
  // Card CONGELADO (histórico reexibido): a data volta a ser LEITURA — um
  // gatilho desabilitado anunciaria um popover que nunca abre.
  if (readOnly) {
    return (
      <div className="genui2 fpl-field fpl-field-when bg-transparent">
        <span className="fpl-flabel">{label}</span>
        <span className={whenMirrorClass(hasOutbound)}>{value}</span>
      </div>
    );
  }
  return (
    <PlannerWhenField
      label={label}
      value={value}
      {...(pendingTail ? { pendingTail } : {})}
      empty={!hasOutbound}
      disabled={false}
      panelLabel={label}
      confirmLabel={confirmDatesLabel}
      confirmDisabled={skirtDisabled}
      open={whenOpen}
      onOpenChange={onWhenOpenChange}
      wanted={wanted}
      icon={<CalendarBlankIcon size={14} aria-hidden />}
      // Somente ida fecha no CLIQUE do dia (o wrapper do pick faz isso) — a
      // saia inteira sai; no intervalo entra o par Redefinir/Confirmar.
      footer={
        oneWay
          ? null
          : datesSkirt({
              hasOutbound,
              confirmDisabled: skirtDisabled,
              confirmLabel: confirmDatesLabel,
              resetLabel,
              resetDates,
              onConfirm,
            })
      }
      onOpen={KEEP_DATES}
      // Sair da folha sem o Confirmar descarta (ver `useSheetSnapshot`); o
      // popover guarda o que foi escolhido.
      onDismiss={sheet ? restoreDates : KEEP_DATES}
      float={float}
      align={align}
      {...(sheet ? { sheet } : {})}
      backLabel={backLabel}
    >
      {children}
    </PlannerWhenField>
  );
}

/**
 * O QUANDO de cada linha do multi como popover SEM saia (#2136): abre por gesto
 * no gatilho da linha (`dateOpenLegId`), nunca sozinho — responder o destino só
 * passa o anel de atenção (`legFocus`) pra data. Escolher o dia grava, fecha e
 * AVANÇA o anel — confirmar N pernas cobraria N cliques de eco. O conteúdo é o
 * calendário da perna em edição, que é sempre a dona do popover aberto.
 */
export function LegWhenField({
  state,
  multi,
  index,
  calendar,
  floatDatesPopover = false,
  sheet,
  cityOfAirport,
  copy,
  locale,
}: {
  readonly state: PlannerState;
  readonly multi: MultiPlanner;
  readonly index: number;
  readonly calendar: ReactNode;
  readonly floatDatesPopover?: boolean;
  /** Ver `PlannerBodyProps.narrow` — a linha do multi só existe no layout aberto. */
  readonly sheet: boolean;
  /** Ver `PlannerFormProps.cityOfAirport` — nomeia o destino no título da folha. */
  readonly cityOfAirport: PlannerBodyProps['cityOfAirport'];
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
}) {
  const leg = state.legs[index];
  const id = state.legIds[index];
  if (!leg || !id) return null;
  // A folha do multi (#3446): barra de meses pregada fora do rolo e SEM rodapé
  // — escolher o dia grava e avança, como no popover; um Confirmar aqui cobraria
  // um toque de eco por perna. O cabeçalho nomeia o trecho ("Ida para Miami" ·
  // "2 de 3 · saindo de JFK"): as abas dizem o modo, não qual perna se edita.
  const sheetSlots: WhenSheetSlots | undefined = sheet
    ? legSheetHeading(leg, index, state.legs.length, copy, cityOfAirport)
    : undefined;
  return (
    <PlannerWhenField
      label={copy.when}
      value={leg.date ? fmtWeekdayDayMonth(leg.date, locale) : copy.whenEmpty}
      empty={!leg.date}
      disabled={false}
      panelLabel={copy.when}
      confirmLabel=""
      confirmDisabled={false}
      // Só a primeira linha nomeia as colunas (#2136) — as demais herdam. No
      // estreito (`sheet`) as linhas viram coluna e toda uma rotula, como os
      // DE/PARA do builder (`MultiLegBuilder.stacked`, #2773).
      hideLabel={index > 0 && !sheet}
      open={id === state.dateOpenLegId}
      onOpenChange={(next) => {
        if (next) {
          // Abrir a data de outra perna é gesto explícito — mesma marcação do
          // `onEdit` (o heat da perna sai como trigger user).
          multi.noteHeatGestureUser();
          state.setEditingLegId(id);
          state.setLegFocus('date');
          state.setDateOpenLegId(id);
          return;
        }
        state.setLegFocus(null);
        state.setDateOpenLegId(null);
      }}
      icon={<CalendarBlankIcon size={14} aria-hidden />}
      footer={null}
      onOpen={KEEP_DATES}
      onDismiss={KEEP_DATES}
      float={floatDatesPopover}
      // A linha do multi mora em `.fpl-leg-open` — align 'end' é a régua de
      // sempre de `.fpl-leg-open .fpl-pop` (CSS), reproduzida em JS pro portal.
      align="end"
      {...(sheetSlots ? { sheet: sheetSlots } : {})}
      backLabel={copy.back}
    >
      {calendar}
    </PlannerWhenField>
  );
}

/** O Confirmar da saia do intervalo só acende com a resposta da FORMA fechada —
 *  a mesma régua de datas do `ready` (ida e volta exige a volta; sem abas, a
 *  ida basta). */
function skirtGate(state: PlannerState, showTabs: boolean): boolean {
  return !state.out || (showTabs && state.activeTripType === 'round_trip' && !state.ret);
}

/** O campo QUANDO binário JÁ montado com a grade dentro — o par calendário +
 *  `WhenFieldView` saiu do `PlannerBody` pelo teto de linhas dele. `openLayout`
 *  liga o modo do #2136 (popover controlado, saia só no intervalo, só-ida fecha
 *  no clique do dia). */
export function BinaryWhenView({
  props,
  openLayout,
}: {
  readonly props: PlannerBodyProps;
  readonly openLayout: boolean;
}) {
  const { state, binary, band, readOnly, loading, choice, best, note } = props;
  const { ctaLabel, confirmDatesLabel, whenLabel, whenPendingTail, copy, locale } = props;
  const { openDates, restoreDates, submitDates, whenOpen, onWhenOpenChange, guide } = props;
  const sheet = props.narrow && openLayout;
  // O Confirmar do layout aberto responde o guia (#4620): busca com a rota dada.
  const confirmDates = () => {
    guide.confirmDates(binary.ready ? submitDates : undefined);
  };
  const confirmLabel = guide.confirmSearches ? copy.confirmAndSearch : confirmDatesLabel;
  const resetDates = () => {
    state.setOut(undefined);
    state.setRet(undefined);
  };
  const binaryCalendar = (
    <BinaryCalendarView
      binary={binary}
      readOnly={readOnly}
      loading={loading}
      choice={choice}
      {...(best ? { best } : {})}
      {...(sheet ? { sheet: binaryWhenSheetFooter(props, resetDates, confirmDates) } : {})}
      // Somente ida fecha no CLIQUE do dia (#2136): o clique já é a decisão
      // inteira, e a saia de confirmação ficou só com o intervalo. Na FOLHA
      // também (#3446): o Confirmar do rodapé só aparece a quem abriu com a
      // data já preenchida (ver `WhenSheetFooter`).
      {...(openLayout && binary.oneWayTab
        ? {
            onPick: (date: string) => {
              binary.pick(date);
              guide.answerDates();
              onWhenOpenChange(false);
            },
          }
        : {})}
      copy={copy}
      locale={locale}
    />
  );
  return (
    <WhenFieldView
      band={band}
      readOnly={readOnly}
      {...(note ? { note } : {})}
      label={copy.when}
      value={whenLabel}
      {...(whenPendingTail ? { pendingTail: whenPendingTail } : {})}
      hasOutbound={Boolean(state.out)}
      ready={binary.ready}
      ctaLabel={ctaLabel}
      confirmDatesLabel={confirmLabel}
      calendar={binaryCalendar}
      openDates={openDates}
      restoreDates={restoreDates}
      submitDates={openLayout ? confirmDates : submitDates}
      open={openLayout}
      oneWay={binary.oneWayTab}
      whenOpen={whenOpen}
      onWhenOpenChange={onWhenOpenChange}
      skirtDisabled={skirtGate(state, binary.showTabs)}
      resetLabel={copy.reset}
      resetDates={resetDates}
      floatDatesPopover={props.floatDatesPopover}
      sheetDates={sheet ? binaryWhenSheetSlots(props) : undefined}
      backLabel={copy.back}
      wanted={props.guide.wanted === 'date'}
    />
  );
}

/** O cabeçalho da FOLHA binária (#3446): o título por modo. Fora de
 *  `BinaryWhenView` pelo teto de linhas. */
function binaryWhenSheetSlots(props: PlannerBodyProps): WhenSheetSlots {
  const { state, binary, copy } = props;
  return {
    title: whenSheetTitle({
      tripType:
        binary.showTabs && state.activeTripType !== 'multi_leg' ? state.activeTripType : undefined,
      destination: state.destination,
      destinationLabel: state.destinationLabel,
      cityOf: props.cityOfAirport,
      copy,
    }),
  };
}

/** O rodapé pregado da FOLHA binária (#3446): a saia de estado — com o "N
 *  datas analisadas" na linha da economia, no desenho da #5662 — e o par
 *  Redefinir/"Confirmar datas". Fechar é fechar a abertura CONTROLADA do campo;
 *  a folha devolve o foco ao gatilho sozinha. */
function binaryWhenSheetFooter(
  props: PlannerBodyProps,
  resetDates: () => void,
  confirmDates: () => void,
): ReactNode {
  const { state, binary, loading, choice, best, copy } = props;
  return (
    <WhenSheetFooter
      status={
        <PlannerStatus
          choice={choice}
          loading={loading}
          loadingLabel={copy.loadingPrices}
          {...(best ? { best } : {})}
          {...(binary.showCalMeta ? { meta: copy.analyzed({ count: binary.analyzed }) } : {})}
          {...(binary.noHeat ? { notice: copy.noPriceTrend } : {})}
          sheet
        />
      }
      hasOutbound={Boolean(state.out)}
      oneWay={binary.oneWayTab}
      confirmDisabled={skirtGate(state, binary.showTabs)}
      resetDates={resetDates}
      searches={props.guide.confirmSearches}
      confirm={() => {
        props.onWhenOpenChange(false);
        confirmDates();
      }}
      copy={copy}
    />
  );
}
